import httpClient from "../../../api/httpClient";
import { WEBSOCKET_URL } from "../../../config";
import { uploadFile } from "../../../utils/uploadFile";
import { handlerReceiveMessage, removeChat, setKeyValue } from "../slice";
import { getChatsById } from "../thunks";

const PING_INTERVAL_MS = 8 * 60 * 1000;

// websocketService.js
class WebSocketService {
  constructor() {
    this.socket = null;
    this.subscribers = 0;
    this.store = null;
    this.isIntentionalClose = false;
    this.pingTimer = null;
  }

  connect(store) {
    this.store = store;
    const dispatch = store.dispatch;

    // Only create connection if it doesn't exist
    if (this.socket === null || this.socket.readyState === WebSocket.CLOSED) {
      this._createSocket(dispatch);
    }

    this.subscribers++;
    console.log(`Subscribers: ${this.subscribers}`);
  }

  async _createSocket(dispatch) {
    const token = await httpClient.getValidAccessToken();

    if (!token) return null;

    this.socket = new WebSocket(
      `${WEBSOCKET_URL}?authorization=Bearer ${token}`,
    );

    this.socket.onopen = () => {
      console.log("WebSocket Connected");

      dispatch(
        setKeyValue({
          key: "isConnected",
          value: true,
        }),
      );

      this._resetPing();
    };

    this.socket.onmessage = (event) => {
      this._resetPing();
      const data = JSON.parse(event.data);
      const socketHandlers = {
        conversationAdded: getChatsById,
        conversationRemoved: removeChat,
      };
      const handler = socketHandlers[data?.type];
      if (handler) {
        dispatch(handler(data));
      } else {
        dispatch(handlerReceiveMessage(data));
      }
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket Error:", error);
    };

    this.socket.onclose = (event) => {
      this._stopPing();
      console.log("WebSocket Disconnected", event?.code, event?.reason);

      dispatch(
        setKeyValue({
          key: "isConnected",
          value: false,
        }),
      );

      this.socket = null;

      // Auto-reconnect immediately if disconnect was NOT intentional
      if (!this.isIntentionalClose && this.subscribers > 0) {
        console.log("WebSocket: Reconnecting...");
        this._createSocket(dispatch);
      }
    };
  }

  _resetPing() {
    this._stopPing();
    this.pingTimer = setTimeout(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        console.log("WebSocket: No activity, sending ping");
        this.socket.send(JSON.stringify({ action: "ping" }));
        this._resetPing();
      }
    }, PING_INTERVAL_MS);
  }

  // ✅ Clear ping timer
  _stopPing() {
    if (this.pingTimer) {
      clearTimeout(this.pingTimer);
      this.pingTimer = null;
    }
  }

  disconnect() {
    this._stopPing();
    this.subscribers--;
    console.log(`Subscribers: ${this.subscribers}`);

    // Only close if no more subscribers
    if (this.subscribers <= 0) {
      this.subscribers = 0;
      this.isIntentionalClose = true;

      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }

      // Reset flag after close
      this.isIntentionalClose = false;
    }
  }

  async send(data) {
    try {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        console.error("WebSocket is not connected");
        return;
      }

      // Normalize to array
      const payloads = Array.isArray(data) ? data : [data];

      for (const payload of payloads) {
        const { tempMessage, ...wsPayload } = payload;

        if (tempMessage?.message_attachment?.file) {
          const url = await uploadFile({
            file: tempMessage.message_attachment.file,
            fields: {
              tenant_id: tempMessage?.tenant_id,
              conversation_id: tempMessage?.conversation_id,
            },
          });
          wsPayload.data.storage_url = url;
        }

        this.socket.send(JSON.stringify(wsPayload));
      }
      this._resetPing();
    } catch (error) {
      console.log(
        "🚀 ~ websocketService.js ~ WebSocketService ~ send ~ error:",
        error,
      );
    }
  }

  getSocket() {
    return this.socket;
  }
}

// Export singleton instance
export const wsService = new WebSocketService();
